import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { ArrowLeft, Search, UsersRound } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { httpServices as services } from "@/services";
import { useAuth } from "@/services/supabase/AuthProvider";
import type { FarmerAllocation } from "@/services/contracts";
import type { StaffDetail } from "@/types/domain";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function StaffFarmerAllocationPage() {
  const auth = useAuth();
  const isSuperAdmin = auth?.aczonContext?.role_code?.toUpperCase() === "SUPER_ADMIN";
  const [allocations, setAllocations] = useState<FarmerAllocation[]>([]);
  const [staff, setStaff] = useState<StaffDetail[]>([]);
  const [assignmentType, setAssignmentType] = useState<"FIELD_OFFICER" | "AGRONOMIST">("FIELD_OFFICER");
  const [employeeId, setEmployeeId] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [rows, staffResult] = await Promise.all([
        services.staff.listFarmerAllocations(),
        services.staff.list({ pageSize: 100 }),
      ]);
      setAllocations(rows);
      setStaff(staffResult.items.filter((person) => person.employmentStatus === "ACTIVE"));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Farmer assignments could not be loaded.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allocations;
    return allocations.filter((row) =>
      `${row.farmerName} ${row.farmerCode} ${row.geographicUnitLabel} ${row.assignedFieldOfficerName || ""} ${row.assignedAgronomistName || ""}`
        .toLowerCase()
        .includes(q)
    );
  }, [allocations, search]);

  const eligibleStaff = useMemo(() => staff.filter((person) =>
    person.assignments.some((assignment) =>
      assignment.status === "ACTIVE" && assignment.roleOnProject.toUpperCase() === assignmentType
    )
  ), [staff, assignmentType]);

  const allVisibleSelected = filtered.length > 0 && filtered.every((row) => selected.has(row.farmerId));

  const toggleFarmer = (id: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleVisible = () => {
    setSelected((current) => {
      const next = new Set(current);
      if (allVisibleSelected) filtered.forEach((row) => next.delete(row.farmerId));
      else filtered.forEach((row) => next.add(row.farmerId));
      return next;
    });
  };

  const save = async () => {
    if (!isSuperAdmin) return;
    if (selected.size === 0) { toast.error("Select at least one farmer."); return; }
    if (!employeeId) { toast.error("Select a staff member, or choose Unassign."); return; }
    setSaving(true);
    try {
      const rows = await services.staff.bulkUpdateFarmerAllocations({
        assignmentType,
        employeeId: employeeId === "UNASSIGN" ? undefined : employeeId,
        farmerIds: Array.from(selected),
      });
      setAllocations(rows);
      setSelected(new Set());
      toast.success("Farmer assignments updated", { description: `${selected.size} farmer${selected.size === 1 ? "" : "s"} updated.` });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Assignments could not be updated.");
    } finally {
      setSaving(false);
    }
  };

  return <div className="space-y-6" data-testid="staff-farmer-allocation-page">
    <PageHeader
      eyebrow="Staff & Users"
      title="Farmer Assignment & Allocation"
      description="Assign many farmers to a Field Officer or Agronomist from one place. Farmer profiles only display the result."
      action={<Button variant="outline" asChild><Link to="/staff"><ArrowLeft className="mr-2 size-4" />Staff directory</Link></Button>}
    />

    {!isSuperAdmin && <Card><CardContent className="p-6"><p className="font-semibold">SUPER_ADMIN access required</p><p className="mt-2 text-sm text-muted-foreground">Farmer-to-staff allocation is an administrative function.</p></CardContent></Card>}

    {isSuperAdmin && <>
      <Card><CardContent className="grid gap-4 p-5 md:grid-cols-3">
        <div className="space-y-2">
          <Label>Assignment type</Label>
          <Select value={assignmentType} onValueChange={(value: "FIELD_OFFICER" | "AGRONOMIST") => { setAssignmentType(value); setEmployeeId(""); }}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="FIELD_OFFICER">Field Officer</SelectItem><SelectItem value="AGRONOMIST">Agronomist</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Staff member</Label>
          <Select value={employeeId} onValueChange={setEmployeeId}>
            <SelectTrigger><SelectValue placeholder="Select staff member" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="UNASSIGN">Unassign selected farmers</SelectItem>
              {eligibleStaff.map((person) => <SelectItem key={person.id} value={person.id}>{person.name} · {assignmentType === "FIELD_OFFICER" ? "Field Officer" : "Agronomist"}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <Button onClick={save} disabled={saving || selected.size === 0 || !employeeId} className="w-full"><UsersRound className="mr-2 size-4" />{saving ? "Updating..." : `Apply to ${selected.size} selected`}</Button>
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-0">
        <div className="flex flex-col gap-3 border-b p-4 sm:flex-row sm:items-center">
          <div className="relative flex-1"><Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" /><Input className="pl-9" value={search} onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)} placeholder="Search farmer, code, location or assigned staff" /></div>
          <Button type="button" variant="outline" onClick={toggleVisible}>{allVisibleSelected ? "Clear visible" : "Select all visible"}</Button>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead className="w-12">Select</TableHead><TableHead>Farmer</TableHead><TableHead>Location</TableHead><TableHead>Field Officer</TableHead><TableHead>Agronomist</TableHead><TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((row) => <TableRow key={row.farmerId}>
                <TableCell><input aria-label={`Select ${row.farmerName}`} type="checkbox" checked={selected.has(row.farmerId)} onChange={() => toggleFarmer(row.farmerId)} className="size-4" /></TableCell>
                <TableCell><p className="font-semibold">{row.farmerName}</p><p className="font-mono text-xs text-muted-foreground">{row.farmerCode}</p></TableCell>
                <TableCell className="max-w-[320px] text-sm">{row.geographicUnitLabel}</TableCell>
                <TableCell>{row.assignedFieldOfficerName || <span className="text-muted-foreground">Unassigned</span>}</TableCell>
                <TableCell>{row.assignedAgronomistName || <span className="text-muted-foreground">Unassigned</span>}</TableCell>
                <TableCell><Badge variant="outline">{row.recordStatus}</Badge></TableCell>
              </TableRow>)}
              {!loading && filtered.length === 0 && <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground">No farmers found.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </CardContent></Card>
    </>}
  </div>;
}
